/**
 * Active work-session panel for a supplied task. Restores an existing session, derives elapsed time
 * from wall-clock timestamps and pauses, and logs work/pause segments, flow markers, and actual
 * outcome values. Concurrent starts share one promise; a retained operation key survives uncertain
 * responses. Cancellation resolves an uncertain start or failed initial read before hiding the
 * session. onMinimize preserves the running session; onComplete and onClose let the parent remove
 * its panel. Finish submits session details and feedback, then updates the task store; failures
 * caught by the handler leave the timer visible. "Add concurrent activity" starts another
 * activity's session alongside this one, sharing a concurrency group on the server.
 *
 * @module apps/web/src/components/immersion/ImmersionMode
 */

import { useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import type { AttributeDef, TaskUI } from '@fluxability/types';
import { Button } from '@/components/ui/button';
import { EnumAttributeInput } from '@/components/attributes/EnumAttributeInput';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { useUserStore } from '@/stores/userStore';
import { loadFluxAgentBrowser } from '@/lib/fluxAgentBrowser';
import { bindSessionLifecycle, claimSession, recoverSession, clearSessionCheckpoint } from '@/lib/sessionLifecycle';
import { refreshPlannerAfterAgent } from '@/components/flux-agent/plannerRefresh';
import { useToast } from '@/hooks/use-toast';
import {
  CheckCircle2,
  Gauge,
  Layers,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  Sparkles,
  Target,
  Waves,
  X,
} from 'lucide-react';
import { cn, formatDuration } from '@/lib/utils';
import { useTasksStore } from '@/stores/tasksStore';
import { useScheduleStore } from '@/stores/scheduleStore';
import { AddConcurrentActivityDialog } from '@/components/immersion/AddConcurrentActivityDialog';

/** Activity-session ownership callbacks distinguish minimizing a running session from removing its panel after close or completion. */
interface ImmersionModeProps {
  /** Activity whose current server session is restored or started. */
  task: TaskUI;
  /** Remove the session panel after the close/cancel flow resolves. */
  onClose: () => void;
  /** Hide the panel while retaining the active work session. */
  onMinimize: () => void;
  /** Remove the panel after recording finish data and updating the activity. */
  onComplete: () => void;
}

/** Timestamped flow marker captured during a work session for later outcome logging. */
interface FlowMarker {
  /** Wall-clock timestamp at which the marker or change was recorded. */
  timestamp: string;
  /** Elapsed session time in seconds when this observation was captured. */
  elapsedSeconds: number;
  /** Reported flow-depth score; marker null means no depth was supplied. */
  depth: number | null;
  /** Free-text note recorded with the flow marker. */
  note: string;
}

/** A recorded change in subjective flow depth during the session. */
interface FlowDepthChange {
  /** Wall-clock timestamp at which the marker or change was recorded. */
  timestamp: string;
  /** Elapsed session time in seconds when this observation was captured. */
  elapsedSeconds: number;
  /** Flow-depth score immediately before the recorded change. */
  previousDepth: number;
  /** Reported flow-depth score; marker null means no depth was supplied. */
  depth: number;
}

/** Format elapsed seconds as a zero-padded clock for the session display; session accounting is calculated separately. */
function formatStopwatch(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/** Draw the clamped 0–1 session-progress fraction around caller-supplied center content. The ring is a view of elapsed work, not the session clock. */
function ProgressRing({ progress, children }: { /** Completion fraction used for the circular stroke; drawing clamps it to 0–1. */
progress: number; /** Timer and action content displayed in the center of the ring. */
children: React.ReactNode }) {
  const size = 236;
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.max(0, Math.min(1, progress)));

  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-white/10"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="text-cyan-300 transition-[stroke-dashoffset] duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        {children}
      </div>
    </div>
  );
}

/** Ordered outcome-score controls mapping draft property names to compact session labels. */
const scoreFields = [
  ['productivityInvestment', 'P investment'],
  ['rejuvenationInvestment', 'RJ investment'],
  ['productivityReturn', 'P return'],
  ['rejuvenationReturn', 'RJ return'],
  ['resistance', 'Resistance'],
  ['focusLevel', 'Focus'],
] as const;

/**
 * Active work-session panel for a supplied task. Restores an existing session, derives elapsed time from wall-clock timestamps and pauses, and logs work/pause segments, flow markers, and actual outcome values. Concurrent starts share one promise; a retained operation key survives uncertain responses. Cancellation resolves an uncertain start or failed initial read before hiding the session. onMinimize preserves the running session; onComplete and onClose let the parent remove its panel. Finish submits session details and feedback, then updates the task store; failures caught by the handler leave the timer visible.
 */
export function ImmersionMode(props: ImmersionModeProps) {
  const ownerId = useUserStore(s => s.userId);
  if (!ownerId) return null;
  if (props.task.userId !== ownerId) return <div role="alert">This activity belongs to another account. <Button onClick={props.onClose}>Close</Button></div>;
  return <OwnedImmersionMode key={`${ownerId}:${props.task.id}`} {...props} ownerId={ownerId} />;
}

/** Account/task-keyed timer state is discarded completely when the authenticated owner changes. */
function OwnedImmersionMode({ task, onClose, onMinimize, onComplete, ownerId }: ImmersionModeProps & { /** Authenticated owner fixed for this mount. */ ownerId: string }) {
  const { toast } = useToast();
  const [recoveryUnavailable, setRecoveryUnavailable] = useState(false);
  const runningRef = useRef(false);
  const ownsSessionRef = useRef(false);
  const [recovered, setRecovered] = useState(false);
  const updateTask = useTasksStore(s => s.updateTask);
  const startImmersionMode = useScheduleStore(s => s.startImmersionMode);
  const [isAddingConcurrent, setIsAddingConcurrent] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [sessionStartedAtMs, setSessionStartedAtMs] = useState<number | null>(null);
  const [pauseSecondsOffset, setPauseSecondsOffset] = useState(0);
  const [clockTick, setClockTick] = useState(() => Date.now());
  const [pauseCount, setPauseCount] = useState(0);
  const [pauseLog, setPauseLog] = useState<
    { timestamp: string; duration: number; reason: string }[]
  >([]);
  const [currentPauseStart, setCurrentPauseStart] = useState<number | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [activeSegmentStart, setActiveSegmentStart] = useState<number | null>(null);
  const [segments, setSegments] = useState<
    Array<{ type: 'work' | 'pause'; start: string; end: string; reason?: string }>
  >([]);
  const [phase, setPhase] = useState<'running' | 'finish'>('running');
  const [isExitPromptOpen, setIsExitPromptOpen] = useState(false);
  const [isSplitFullscreen, setIsSplitFullscreen] = useState(false);
  const [flowMarkers, setFlowMarkers] = useState<FlowMarker[]>([]);
  const [flowDepth, setFlowDepth] = useState(7);
  const [flowDepthChanged, setFlowDepthChanged] = useState(false);
  const [flowDepthChanges, setFlowDepthChanges] = useState<FlowDepthChange[]>([]);
  const flowDepthChangesRef = useRef<FlowDepthChange[]>([]);
  const [completionPercent, setCompletionPercent] = useState(task.completionPercent ?? 0);
  const [notes, setNotes] = useState('');
  const [emotionWord, setEmotionWord] = useState(String(task.customAttributes?.emotion_word || ''));
  const [attributes, setAttributes] = useState<AttributeDef[]>([]);
  const [actualState, setActualState] = useState({
    productivityInvestment: task.stateProfile?.required_productivity_energy ?? 5,
    rejuvenationInvestment: task.stateProfile?.required_rejuvenation_energy ?? 5,
    productivityReturn: task.stateProfile?.productivity_return ?? 5,
    rejuvenationReturn: task.stateProfile?.rejuvenation_return ?? 5,
    resistance: task.stateProfile?.resistance ?? 5,
    focusLevel: 7,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const flowInputRef = useRef<HTMLInputElement | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const startRequestRef = useRef<Promise<string> | null>(null);
  const startKeyRef = useRef<string | null>(null);
  const autoStartAttempted = useRef(false);
  const cancellingRef = useRef(false);
  const [sessionLoadError, setSessionLoadError] = useState(false);
  const [sessionLoadMessage, setSessionLoadMessage] = useState('Could not load the active session.');

  const readDraft = useEffectEvent(() => ({ notes, emotionWord, completionPercent, actualState, flowDepth, flowDepthChanged, flowMarkers, flowDepthChanges, segments, pauseLog }));
  /** Restore only known UI form fields; the server remains authoritative for timer state. */
  const restoreDraft = useEffectEvent((draft?: Record<string, unknown>) => {
    if (!draft) return;
    if (typeof draft.notes === 'string') setNotes(draft.notes);
    if (typeof draft.emotionWord === 'string') setEmotionWord(draft.emotionWord);
    if (typeof draft.completionPercent === 'number') setCompletionPercent(draft.completionPercent);
    if (typeof draft.flowDepth === 'number') setFlowDepth(draft.flowDepth);
    if (typeof draft.flowDepthChanged === 'boolean') setFlowDepthChanged(draft.flowDepthChanged);
    if (Array.isArray(draft.flowMarkers)) setFlowMarkers(draft.flowMarkers);
    if (Array.isArray(draft.flowDepthChanges)) {
      setFlowDepthChanges(draft.flowDepthChanges);
      flowDepthChangesRef.current = draft.flowDepthChanges;
    }
    if (Array.isArray(draft.segments)) setSegments(draft.segments);
    if (Array.isArray(draft.pauseLog)) setPauseLog(draft.pauseLog);
    if (draft.actualState && typeof draft.actualState === 'object') {
      const values = draft.actualState as typeof actualState;
      if (scoreFields.every(([field]) => typeof values[field] === 'number')) setActualState(values);
    }
  });

  useEffect(() => {
    let disposed = false;
    let release: (() => void) | null = null;
    const checkActiveSession = async () => {
      try {
        if (!ownerId) throw new Error('Sign in before starting a timer.');
        release = await claimSession(ownerId, task.id);
        if (disposed) { release?.(); return; }
        if (!release) throw new Error('This activity timer is open in another tab.');
        ownsSessionRef.current = true;
        await loadFluxAgentBrowser();
        if (disposed) return;
        let response = await api.training.getActiveImmersions();
        const existing = Array.isArray(response.data) ? response.data.find(session => session.task_id === task.id) : null;
        if (disposed) return;
        if (existing) {
          const recovery = await recoverSession(ownerId, existing.id);
          if (disposed) return;
          if (recovery.unavailable) setRecoveryUnavailable(true);
          if (recovery.recovered) {
            setRecovered(true);
            restoreDraft(recovery.draft);
            response = await api.training.getActiveImmersions();
          }
        }
        if (disposed) return;
        if (!response.success) throw new Error(response.error?.message || 'Could not load session');
        const activeSession = Array.isArray(response.data)
          ? response.data.find(session => session.task_id === task.id)
          : null;
        if (response.success && activeSession) {
          sessionIdRef.current = activeSession.id;
          setSessionId(activeSession.id);
          const startTime = new Date(activeSession.start_time).getTime();
          const totalPauseSeconds = Array.isArray(activeSession.pauseEvents)
            ? activeSession.pauseEvents.reduce((acc: number, p) => acc + (p.duration || 0), 0)
            : 0;
          setSessionStartedAtMs(startTime);
          setPauseSecondsOffset(totalPauseSeconds);
          setClockTick(Date.now());
          const paused = activeSession.runtimeState.phase === 'paused';
          setCurrentPauseStart(paused ? Date.parse(activeSession.runtimeState.pausedAt ?? activeSession.start_time) : null);
          setIsRunning(!paused);
          runningRef.current = !paused;
          setActiveSegmentStart(paused ? null : Date.now());
        }
      } catch (error) {
        if (!disposed) {
          setSessionLoadError(true);
          setSessionLoadMessage(error instanceof Error ? error.message : 'Could not load the active session.');
        }
        console.error('Failed to fetch active session:', error);
      } finally {
        if (!disposed) setIsLoadingSession(false);
      }
    };
    void checkActiveSession();
    return () => {
      disposed = true;
      ownsSessionRef.current = false;
      release?.();
    };
  }, [task.id, ownerId]);

  useEffect(() => {
    let cancelled = false;
    async function loadAttributes() {
      try {
        const response = await api.user.getAttributes();
        if (!cancelled && response.success && Array.isArray(response.data)) {
          setAttributes(response.data);
          const emotionAttr = response.data.find(isEmotionAttribute);
          setEmotionWord(String(getEmotionValue(task.customAttributes || {}, emotionAttr) || ''));
        }
      } catch (error) {
        console.error('Failed to load immersion attributes:', error);
      }
    }
    void loadAttributes();
    return () => {
      cancelled = true;
    };
  }, [task.customAttributes]);

  useEffect(() => {
    if (!ownerId || !sessionId || isLoadingSession) return;
    return bindSessionLifecycle(ownerId, sessionId, () => runningRef.current, readDraft, () => useUserStore.getState().userId, () => setRecoveryUnavailable(true));
  }, [ownerId, sessionId, isLoadingSession]);

  const emotionAttribute = useMemo(() => attributes.find(isEmotionAttribute), [attributes]);

  /** Derive elapsed work from wall-clock start and pause offsets so background-tab timer throttling does not accumulate clock drift. */
  const elapsedTime = useMemo(() => {
    if (!sessionStartedAtMs) return 0;
    const effectiveNow = currentPauseStart ?? clockTick;
    return Math.max(0, Math.floor((effectiveNow - sessionStartedAtMs) / 1000) - pauseSecondsOffset);
  }, [clockTick, currentPauseStart, pauseSecondsOffset, sessionStartedAtMs]);

  useEffect(() => {
    if (!sessionStartedAtMs || (!isRunning && !currentPauseStart)) return;
    const updateClock = () => setClockTick(Date.now());
    updateClock();
    const interval = window.setInterval(updateClock, 1000);
    window.addEventListener('focus', updateClock);
    document.addEventListener('visibilitychange', updateClock);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', updateClock);
      document.removeEventListener('visibilitychange', updateClock);
    };
  }, [currentPauseStart, isRunning, sessionStartedAtMs]);

  const progress = useMemo(
    () => (task.estimatedDuration ? Math.min(elapsedTime / (task.estimatedDuration * 60), 1) : 0),
    [elapsedTime, task.estimatedDuration]
  );

  /** Share one in-flight session start and retain its operation key after uncertain delivery. Only definitive readiness/validation rejections clear the key; an accepted response establishes the session ID and placement. */
  function ensureSession(): Promise<string> {
    if (!ownsSessionRef.current) return Promise.reject(new Error('This tab does not own the timer.'));
    if (sessionIdRef.current) return Promise.resolve(sessionIdRef.current);
    if (startRequestRef.current) return startRequestRef.current;
    // Retain the operation key after a timeout: the server may have committed
    // the start even when its response was lost.
    startKeyRef.current ??= crypto.randomUUID();
    setIsStarting(true);
    const pending = (async () => {
      const response = await api.training.startImmersion(
        task.id,
        task.estimatedDuration || 30,
        true,
        startKeyRef.current!
      );
      if (!response.success || !response.data?.sessionId) {
        if (
          [
            'ACTIVITY_NOT_READY',
            'ACTIVITY_NOT_SCHEDULED',
            'ACTIVITY_NOT_FOUND',
            'VALIDATION_ERROR',
          ].includes(response.error?.code || '')
        ) {
          startKeyRef.current = null;
        }
        throw new Error(response.error?.message || 'Could not start session');
      }
      const id = response.data.sessionId as string;
      sessionIdRef.current = id;
      setSessionId(id);
      const startedAt = response.data.startTime
        ? new Date(response.data.startTime).getTime()
        : Date.now();
      setSessionStartedAtMs(startedAt);
      setPauseSecondsOffset(0);
      setClockTick(Date.now());
      const slot = response.data.schedule;
      if (slot?.start && slot?.end) {
        updateTask(task.id, {
          status: 'in_progress',
          scheduledStart: slot.start,
          scheduledEnd: slot.end,
        });
      }
      return id;
    })();
    startRequestRef.current = pending;
    void pending
      .finally(() => {
        startRequestRef.current = null;
        setIsStarting(false);
      })
      .catch(() => {});
    return pending;
  }

  /** Start another activity alongside this session, then open its panel; the panel restores the new server session. Failures keep the picker open. */
  async function startConcurrentActivity(other: TaskUI) {
    try {
      const withSessionId = await ensureSession();
      const response = await api.training.startImmersion(
        other.id,
        other.estimatedDuration || 30,
        true,
        crypto.randomUUID(),
        withSessionId
      );
      if (!response.success || !response.data?.sessionId)
        throw new Error(response.error?.message || 'Could not start the activity');
      const slot = response.data.schedule;
      updateTask(other.id, {
        status: 'in_progress',
        ...(slot?.start && slot?.end ? { scheduledStart: slot.start, scheduledEnd: slot.end } : {}),
      });
      startImmersionMode(other.id);
      setIsAddingConcurrent(false);
      toast({ title: 'Concurrent activity started', description: `${other.title} is running alongside ${task.title}.` });
    } catch (error) {
      toast({
        title: 'Could not start concurrent activity',
        description: error instanceof Error ? error.message : 'Try again.',
        variant: 'destructive',
      });
    }
  }

  /** Start or resume only after the initial session lookup succeeded and cancellation is inactive. Resuming awaits the explicit server transition before restarting local work timing. */
  async function handleStart() {
    if (cancellingRef.current || isStarting || sessionLoadError) return;
    setIsStarting(true);
    try {
      await ensureSession();
      if (cancellingRef.current) return;

      const resumeAt = Date.now();
      if (currentPauseStart && sessionId) {
        const pauseDuration = Math.floor((resumeAt - currentPauseStart) / 1000);
        const resumed = await api.training.pauseImmersion(sessionId, pauseDuration, 'break', {
          action: 'resume', occurredAt: new Date(resumeAt).toISOString(), idempotencyKey: crypto.randomUUID(),
        });
        if (!resumed.success) throw new Error(resumed.error?.message ?? 'Could not resume the timer.');
        setPauseLog(prev => [
          ...prev,
          {
            timestamp: new Date(currentPauseStart).toISOString(),
            duration: pauseDuration,
            reason: 'break',
          },
        ]);
        setSegments(prev => [
          ...prev,
          {
            type: 'pause',
            start: new Date(currentPauseStart).toISOString(),
            end: new Date(resumeAt).toISOString(),
            reason: 'break',
          },
        ]);
        setPauseSecondsOffset(resumed.data?.runtimeState.accumulatedPauseSeconds ?? pauseSecondsOffset + pauseDuration);
        setCurrentPauseStart(null);
      }

      if (!sessionStartedAtMs) setSessionStartedAtMs(resumeAt);
      setClockTick(resumeAt);
      setIsRunning(true);
      runningRef.current = true;
      setRecovered(false);
      setActiveSegmentStart(resumeAt);
    } catch (error) {
      console.error('Failed to start immersion session:', error);
      if (!cancellingRef.current)
        toast({
          title: 'Could not start activity',
          description: error instanceof Error ? error.message : 'Try again.',
          variant: 'destructive',
        });
    } finally {
      setIsStarting(false);
    }
  }

  // Start with the latest form state; only the end of the session lookup triggers it.
  const autoStart = useEffectEvent(() => {
    void handleStart();
  });
  useEffect(() => {
    if (!isLoadingSession && !sessionLoadError && !sessionId && !autoStartAttempted.current) {
      autoStartAttempted.current = true;
      autoStart();
    }
  }, [isLoadingSession, sessionLoadError, sessionId]);

  /** Pause only after acknowledgement; retries project the server's original pause boundary. */
  async function handlePause(reason = 'break') {
    let pauseStart = Date.now();
    if (!sessionId || isStarting) return;
    setIsStarting(true);
    try {
      const response = await api.training.pauseImmersion(sessionId, 0, reason, {
        action: 'pause', occurredAt: new Date(pauseStart).toISOString(), idempotencyKey: crypto.randomUUID(),
      });
      if (!response.success) throw new Error(response.error?.message ?? 'Could not pause timer.');
      pauseStart = response.data?.runtimeState.pausedAt ? Date.parse(response.data.runtimeState.pausedAt) : pauseStart;
      runningRef.current = false;
      if (activeSegmentStart) {
        setSegments(prev => [...prev, { type: 'work', start: new Date(activeSegmentStart).toISOString(), end: new Date(pauseStart).toISOString() }]);
      }
      setIsRunning(false);
      setPauseCount(prev => prev + 1);
      setCurrentPauseStart(pauseStart);
      setClockTick(pauseStart);
      setActiveSegmentStart(null);
    } catch (error) {
      toast({ title: 'Could not pause timer', description: error instanceof Error ? error.message : 'Try again.', variant: 'destructive' });
    } finally { setIsStarting(false); }
  }

  function markFlowState() {
    const note = flowInputRef.current?.value.trim() || '';
    setFlowMarkers(prev => [
      ...prev,
      {
        timestamp: new Date().toISOString(),
        elapsedSeconds: elapsedTime,
        depth: flowDepthChanged ? flowDepth : null,
        note,
      },
    ]);
    if (flowInputRef.current) flowInputRef.current.value = '';
    toast({ title: 'Flow state marked', description: `Captured at ${formatStopwatch(elapsedTime)}.` });
  }

  /** Clamp an explicit depth choice to 0–10 and record changed values with both wall-clock and elapsed-work timestamps. */
  function handleFlowDepthChange(nextDepth: number) {
    const depth = Math.max(0, Math.min(10, nextDepth));
    if (depth === flowDepth) return;
    const timestamp = new Date().toISOString();
    const change = {
      timestamp,
      elapsedSeconds: elapsedTime,
      previousDepth: flowDepth,
      depth,
    };
    flowDepthChangesRef.current = [...flowDepthChangesRef.current, change];
    setFlowDepthChanges(flowDepthChangesRef.current);
    setFlowDepth(depth);
    setFlowDepthChanged(true);
  }

  /** Stop the local work segment and open outcome collection. This transition alone does not submit a server completion. */
  async function beginFinish() {
    if (isRunning) await handlePause();
    if (runningRef.current) return;
    setIsExitPromptOpen(false);
    const stopTime = Date.now();
    setClockTick(stopTime);
    setActiveSegmentStart(null);
    setIsRunning(false);
    setPhase('finish');
  }

  function handleExitClick() {
    setIsExitPromptOpen(true);
  }

  /** Hide the panel through its owner while retaining the running session and its durable identity. */
  function handleMinimize() {
    setIsExitPromptOpen(false);
    setIsSplitFullscreen(false);
    onMinimize();
  }

  function handleAttributeUpdated(updated: AttributeDef) {
    setAttributes(current => current.map(attr => (attr.attr_id === updated.attr_id ? updated : attr)));
  }

  /** Submit session-end and feedback requests, then project final progress into the task store, refresh the planner and close through onComplete. A rejected request or an unsuccessful session-end envelope shows an error and keeps the timer visible. The activity's planned slot is left to the server's re-solve, which moves unfinished work later. */
  async function submitFinish(completed: boolean) {
    setIsSubmitting(true);
    const now = new Date().toISOString();
    const percent = completed ? 100 : Math.max(0, Math.min(99, completionPercent));
    const completionBefore = task.completionPercent ?? 0;
    const finalSegments = [...segments];

    try {
      if (sessionId) {
        const ended = await api.training.endImmersion(sessionId, completed, actualState.productivityInvestment, {
          completionPercent: percent,
          completionBefore,
          focusLevel: actualState.focusLevel,
          actualState,
          emotionWord,
          flowMarkers,
          flowDepthChanges: flowDepthChanged ? flowDepthChangesRef.current : [],
          notes,
          segments: finalSegments,
        });
        if (!ended.success) throw new Error(ended.error?.message || 'The session could not be saved');
      }

      await api.training.submitFeedback({
        taskId: task.id,
        scheduledStart: task.scheduledStart,
        scheduledEnd: task.scheduledEnd,
        actualStart: finalSegments[0]?.start,
        actualEnd: now,
        completed,
        actualDuration: elapsedTime / 60,
        energyLevel: actualState.productivityInvestment,
        focusLevel: Math.max(1, Math.min(5, Math.round(actualState.focusLevel / 2))),
        pauseCount,
        pauseLog: pauseLog.map(p => ({ timestamp: p.timestamp, durationSeconds: p.duration })),
        notes: JSON.stringify({
          actualState,
          emotionWord,
          flowMarkers,
          flowDepthChanges: flowDepthChanged ? flowDepthChangesRef.current : [],
          notes,
        }),
        userFeedback: { wasAccurate: true, notes },
      });

      const remainingDuration = completed
        ? 0
        : Math.max(15, Math.round((task.estimatedDuration || 30) * (100 - percent) / 100));
      updateTask(task.id, {
        status: completed ? 'completed' : percent > 0 ? 'in_progress' : 'todo',
        completionPercent: percent,
        remainingDuration,
        stateProfile: {
          ...(task.stateProfile || {}),
          required_productivity_energy: actualState.productivityInvestment,
          required_rejuvenation_energy: actualState.rejuvenationInvestment,
          productivity_return: actualState.productivityReturn,
          rejuvenation_return: actualState.rejuvenationReturn,
          resistance: actualState.resistance,
        },
        customAttributes: {
          ...(task.customAttributes || {}),
          emotion_word: emotionWord || null,
          latest_session: {
            completion_before: completionBefore,
            completion_after: percent,
            completion_delta: Math.max(0, percent - completionBefore),
            actual_duration_minutes: Math.max(1, Math.round(elapsedTime / 60)),
            emotion_word: emotionWord || null,
            ended_at: now,
          },
        },
      });

      toast({
        title: completed ? 'Activity completed' : 'Activity updated',
        description: 'Actual duration, flow, P/RJ, and resistance were captured.',
      });
      // The finished part shows as its logged work segment; the server re-solves the rest of the
      // activity into a later slot and broadcasts it, and this read picks up what is ready now.
      void refreshPlannerAfterAgent();
      if (ownerId && sessionIdRef.current) clearSessionCheckpoint(ownerId, sessionIdRef.current);
      onComplete();
    } catch (error) {
      console.error('Failed to finish immersion session:', error);
      toast({ title: 'Could not save activity data', description: 'Your timer is still visible. Try again.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  }

  /** Resolve any uncertain start or failed initial lookup before cancellation. Keep the panel visible on unresolved failure; close only after cancellation succeeds or absence of a session is established. */
  async function cancelSession() {
    if (!ownsSessionRef.current) { onClose(); return; }
    if (cancellingRef.current || isSubmitting) return;
    cancellingRef.current = true;
    setIsCancelling(true);
    setIsSubmitting(true);
    try {
      let id = sessionIdRef.current;
      if (!id && startKeyRef.current) {
        try {
          id = await ensureSession();
        } catch (error) {
          // A definitive rejected start has nothing to cancel. An uncertain
          // response retains its key and must be retried, never hidden.
          if (startKeyRef.current) throw error;
        }
      }
      // A failed initial read is not proof that no session exists.
      if (!id && sessionLoadError) {
        const active = await api.training.getActiveImmersions();
        if (!active.success)
          throw new Error(active.error?.message || 'Could not confirm session status');
        id = active.data?.find(session => session.task_id === task.id)?.id ?? null;
        sessionIdRef.current = id;
        setSessionId(id);
      }
      if (id) {
        const response = await api.training.cancelImmersion(id);
        if (!response.success)
          throw new Error(response.error?.message || 'Could not cancel session');
      }
      updateTask(task.id, {
        status:
          (task.completionPercent ?? 0) >= 100
            ? 'completed'
            : (task.completionPercent ?? 0) > 0
              ? 'in_progress'
              : 'todo',
      });
      toast({
        title: 'Session cancelled',
        description: 'No completion data was saved for this activity session.',
      });
      setIsRunning(false);
      setIsExitPromptOpen(false);
      if (ownerId && sessionIdRef.current) clearSessionCheckpoint(ownerId, sessionIdRef.current);
      onClose();
    } catch (error) {
      console.error('Failed to cancel immersion session:', error);
      toast({
        title: 'Could not cancel session',
        description: error instanceof Error ? error.message : 'Try again.',
        variant: 'destructive',
      });
    } finally {
      cancellingRef.current = false;
      setIsCancelling(false);
      setIsSubmitting(false);
    }
  }

  if (isLoadingSession) {
    return (
      <Card className="border-cyan-300/30 bg-slate-950 text-white shadow-2xl">
        <CardContent className="flex items-center gap-3 p-4">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-cyan-300 border-b-transparent" />
          <p className="text-sm text-white/70">Starting activity...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <Card
      className={cn(
        'flex min-h-0 flex-1 flex-col overflow-hidden border-white/10 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.18),transparent_38%),linear-gradient(145deg,#07111f,#111827_58%,#151515)] text-white shadow-2xl md:flex-none',
        isSplitFullscreen && 'fixed inset-0 z-[80] h-dvh w-dvw rounded-none border-0'
      )}
    >
      <CardContent className={cn('native-scroll min-h-0 flex-1 space-y-4 overflow-y-auto p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]', isSplitFullscreen && 'flex min-h-dvh flex-col p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-[calc(1rem+env(safe-area-inset-top))] sm:p-6 lg:p-8')}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Badge className={cn('border-white/10', isRunning ? 'bg-cyan-300 text-slate-950' : 'bg-white/10 text-white')}>
                {isRunning ? 'In flow' : phase === 'finish' ? 'Wrap-up' : recovered ? 'Recovered · review and save' : 'Paused'}
              </Badge>
              {task.estimatedDuration && (
                <Badge variant="outline" className="border-white/15 text-white/75">
                  <Target className="mr-1 h-3 w-3" />
                  {formatDuration(task.estimatedDuration)}
                </Badge>
              )}
            </div>
            <h2 className="truncate text-lg font-semibold tracking-normal">{task.title}</h2>
            <p className="text-xs text-white/55">Concurrent tracking is on. Capture reality, not the plan.</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
              onClick={() => setIsSplitFullscreen(value => !value)}
              aria-label={isSplitFullscreen ? 'Exit split-screen full screen' : 'Open split-screen full screen'}
              title={isSplitFullscreen ? 'Exit full screen' : 'Split-screen full screen'}
            >
              {isSplitFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
              onClick={handleMinimize}
              disabled={isSubmitting || isStarting}
              aria-label="Minimize immersive session"
              title="Minimize"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
              onClick={handleExitClick}
              aria-label="Finish or cancel immersive session"
              title="Finish or cancel"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {recoveryUnavailable && <p role="alert" className="mb-3 text-sm text-amber-200">Browser storage is unavailable. Keep this tab open and save your session before closing; crash recovery cannot preserve this draft.</p>}
        {sessionLoadError && (
          <p role="alert" className="mb-3 text-sm text-amber-200">{sessionLoadMessage}</p>
        )}
        {phase === 'running' ? (
          <div
            className={cn(
              'space-y-4',
              isSplitFullscreen &&
                'grid flex-1 items-center gap-6 space-y-0 lg:grid-cols-[minmax(0,1fr)_minmax(360px,480px)]'
            )}
          >
            <div className={cn('space-y-4', isSplitFullscreen && 'flex min-h-[520px] flex-col justify-center')}>
              <ProgressRing progress={progress}>
                <span
                  className={cn(
                    'font-mono text-5xl font-semibold tabular-nums',
                    isSplitFullscreen && 'text-6xl sm:text-7xl'
                  )}
                >
                  {formatStopwatch(elapsedTime)}
                </span>
                <span className="mt-2 text-xs text-white/55">
                  {Math.round(progress * 100)}% of estimate
                </span>
              </ProgressRing>

              <div className="grid grid-cols-3 gap-2">
                {isRunning ? (
                  <Button type="button" variant="secondary" disabled={isSubmitting || isStarting} onClick={() => handlePause('break')}>
                    <Pause className="mr-2 h-4 w-4" />
                    Pause
                  </Button>
                ) : (
                  <Button type="button" onClick={handleStart} disabled={isStarting || isSubmitting || sessionLoadError}>
                    <Play className="mr-2 h-4 w-4" />
                    Resume
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11 border-white/15 bg-white/5 text-white hover:bg-white/10"
                  onClick={beginFinish}
                  disabled={isSubmitting || isStarting || sessionLoadError}
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Finish
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="min-h-11 text-white hover:bg-white/10"
                  onClick={cancelSession}
                  disabled={isSubmitting}
                >
                  <X className="mr-2 h-4 w-4" />
                  {isCancelling ? 'Cancelling…' : 'Cancel'}
                </Button>
              </div>
              <Button
                type="button"
                variant="ghost"
                className="min-h-11 w-full text-white/80 hover:bg-white/10 hover:text-white"
                onClick={() => setIsAddingConcurrent(true)}
                disabled={isSubmitting || isStarting || sessionLoadError}
              >
                <Layers className="mr-2 h-4 w-4" />
                Add concurrent activity
              </Button>
            </div>

            <div className={cn('space-y-4', isSplitFullscreen && 'rounded-lg border border-white/10 bg-white/[0.04] p-4')}>
              <div className="grid grid-cols-3 gap-2 text-center">
                <Metric icon={<Pause className="h-3.5 w-3.5" />} label="Pauses" value={String(pauseCount)} />
                <Metric icon={<Sparkles className="h-3.5 w-3.5" />} label="Flow marks" value={String(flowMarkers.length)} />
                <Metric icon={<Waves className="h-3.5 w-3.5" />} label="Depth changes" value={String(flowDepthChanges.length)} />
              </div>

              <div className="rounded-md border border-white/10 bg-white/[0.05] p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <Label className="text-xs text-white/70">Flow depth</Label>
                  <span className="font-mono text-xs text-cyan-200">{flowDepth}/10</span>
                </div>
                <Input
                  type="range"
                  min={0}
                  max={10}
                  value={flowDepth}
                  onChange={event => handleFlowDepthChange(Number(event.target.value))}
                  className="h-8"
                />
                <p className="mt-1 text-[11px] text-white/45">
                  {flowDepthChanged
                    ? 'Depth changes are being tracked for this session.'
                    : 'Unchanged depth is ignored, so the default does not become training data.'}
                </p>
                <div className="mt-2 flex gap-2">
                  <Input
                    ref={flowInputRef}
                    placeholder="Optional flow note"
                    className="border-white/10 bg-black/20 text-white placeholder:text-white/35"
                  />
                  <Button type="button" onClick={markFlowState} className="shrink-0 bg-cyan-300 text-slate-950 hover:bg-cyan-200">
                    <Waves className="mr-2 h-4 w-4" />
                    Mark
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={cn(
              'space-y-4',
              isSplitFullscreen &&
                'grid flex-1 items-start gap-6 space-y-0 lg:grid-cols-[minmax(280px,420px)_minmax(0,1fr)]'
            )}
          >
            <div className="space-y-4">
              <ProgressRing progress={progress}>
                <span className="font-mono text-4xl font-semibold tabular-nums">{formatStopwatch(elapsedTime)}</span>
                <span className="mt-2 text-xs text-white/55">
                  {Math.max(1, Math.round(elapsedTime / 60))} actual minutes
                </span>
              </ProgressRing>
              <div className="grid grid-cols-3 gap-2 text-center">
                <Metric icon={<Pause className="h-3.5 w-3.5" />} label="Pauses" value={String(pauseCount)} />
                <Metric icon={<Sparkles className="h-3.5 w-3.5" />} label="Flow marks" value={String(flowMarkers.length)} />
                <Metric icon={<Waves className="h-3.5 w-3.5" />} label="Depth changes" value={String(flowDepthChanges.length)} />
              </div>
              <div className="rounded-md border border-cyan-300/20 bg-cyan-300/10 p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-cyan-100">
                  <Gauge className="h-4 w-4" />
                  Actual session telemetry
                </div>
                <p className="mt-1 text-xs text-cyan-50/70">
                  These numbers train the scheduler on what this Activity really cost and returned.
                </p>
              </div>
            </div>

            <div className={cn('space-y-4', isSplitFullscreen && 'rounded-lg border border-white/10 bg-white/[0.04] p-4')}>
            {!isSplitFullscreen && (
              <div className="rounded-md border border-cyan-300/20 bg-cyan-300/10 p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-cyan-100">
                <Gauge className="h-4 w-4" />
                Actual session telemetry
              </div>
              <p className="mt-1 text-xs text-cyan-50/70">
                These numbers train the scheduler on what this Activity really cost and returned.
              </p>
            </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              {scoreFields.map(([key, label]) => (
                <label key={key} className="space-y-1.5">
                  <span className="text-xs text-white/65">{label}</span>
                  <Input
                    type="number"
                    min={0}
                    max={10}
                    value={actualState[key]}
                    onChange={event =>
                      setActualState(prev => ({
                        ...prev,
                        [key]: Math.max(0, Math.min(10, Number(event.target.value))),
                      }))
                    }
                    className="border-white/10 bg-black/20 text-white"
                  />
                </label>
              ))}
            </div>

            <label className="space-y-1.5">
              <span className="text-xs text-white/65">Overall completion after this session</span>
              <Input
                type="number"
                min={0}
                max={100}
                value={completionPercent}
                onChange={event => setCompletionPercent(Math.max(0, Math.min(100, Number(event.target.value))))}
                className="border-white/10 bg-black/20 text-white"
              />
              <Input
                type="range"
                min={0}
                max={100}
                value={completionPercent}
                onChange={event => setCompletionPercent(Math.max(0, Math.min(100, Number(event.target.value))))}
                className="h-8"
              />
              <p className="text-[11px] text-white/45">
                Started at {task.completionPercent ?? 0}%. This session adds{' '}
                {Math.max(0, completionPercent - (task.completionPercent ?? 0))}% if saved.
              </p>
            </label>

            <label className="space-y-1.5">
              <span className="text-xs text-white/65">Emotion word</span>
              {emotionAttribute ? (
                <div className="[&_button]:border-white/10 [&_button]:bg-black/20 [&_button]:text-white">
                  <EnumAttributeInput
                    attr={emotionAttribute}
                    value={emotionWord}
                    onChange={setEmotionWord}
                    onAttributeUpdated={handleAttributeUpdated}
                  />
                </div>
              ) : (
                <Input
                  value={emotionWord}
                  onChange={event => setEmotionWord(event.target.value)}
                  placeholder="Thoughtful, resistant, excited..."
                  className="border-white/10 bg-black/20 text-white placeholder:text-white/35"
                />
              )}
            </label>

            <label className="space-y-1.5">
              <span className="text-xs text-white/65">Notes</span>
              <Textarea
                value={notes}
                onChange={event => setNotes(event.target.value)}
                placeholder="What changed your state? What made starting easier or harder?"
                className="border-white/10 bg-black/20 text-white placeholder:text-white/35"
              />
            </label>

            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                onClick={() => submitFinish(true)}
                disabled={isSubmitting || isStarting}
                className="min-h-11 bg-cyan-300 text-slate-950 hover:bg-cyan-200"
              >
                Complete activity
              </Button>
              <Button
                type="button"
                onClick={() => submitFinish(false)}
                disabled={isSubmitting || isStarting}
                variant="outline"
                className="min-h-11 border-white/15 bg-white/5 text-white hover:bg-white/10"
              >
                Save progress
              </Button>
            </div>
            <Button
              type="button"
              variant="ghost"
              className="min-h-11 w-full text-white hover:bg-white/10"
              onClick={cancelSession}
              disabled={isSubmitting}
            >
              <X className="mr-2 h-4 w-4" />
              {isCancelling ? 'Cancelling…' : 'Cancel session'}
            </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    <Dialog open={isExitPromptOpen} onOpenChange={setIsExitPromptOpen}>
      <DialogContent className="border-white/10 bg-slate-950 text-white sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Finish or cancel?</DialogTitle>
          <DialogDescription className="text-white/60">
            Minimize keeps this Activity Session running. Cancel stops it without saving completion data.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:space-x-0">
          <Button
            type="button"
            variant="ghost"
            className="text-white hover:bg-white/10"
            onClick={handleMinimize}
            disabled={isSubmitting || isStarting}
          >
            <Minimize2 className="mr-2 h-4 w-4" />
            Minimize
          </Button>
          <Button
            type="button"
            variant="outline"
            className="border-white/15 bg-white/5 text-white hover:bg-white/10"
            onClick={cancelSession}
            disabled={isSubmitting}
          >
            <X className="mr-2 h-4 w-4" />
            {isCancelling ? 'Cancelling…' : 'Cancel session'}
          </Button>
          <Button
            type="button"
            className="bg-cyan-300 text-slate-950 hover:bg-cyan-200"
            onClick={beginFinish}
            disabled={isSubmitting || isStarting}
          >
            <CheckCircle2 className="mr-2 h-4 w-4" />
            Finish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <AddConcurrentActivityDialog
      open={isAddingConcurrent}
      onOpenChange={setIsAddingConcurrent}
      currentTaskId={task.id}
      onStart={startConcurrentActivity}
    />
    </>
  );
}

/** Display a supplied session metric with its icon and label. Time and outcome calculations remain in the immersion-session owner. */
function Metric({
  icon,
  label,
  value,
}: {
  /** Icon component rendered beside the visible caption. */
  icon: React.ReactNode;
  /** Visible caption describing this control or metric. */
  label: string;
  /** Preformatted metric value displayed without additional measurement. */
  value: string;
}) {
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.05] p-2">
      <div className="flex items-center justify-center gap-1 text-[10px] uppercase text-white/45">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-mono text-lg text-white">{value}</div>
    </div>
  );
}

/** Recognize the emotion field after normalizing its display name, keeping historical field naming compatible with session outcome logging. */
function isEmotionAttribute(attr: AttributeDef): boolean {
  return normalizeAttributeName(attr.name) === 'emotion';
}

/** Normalize an outcome-field name to lowercase underscore-separated text before identifying special emotion handling. */
function normalizeAttributeName(name: unknown): string {
  return String(name ?? '').trim().toLowerCase().replace(/\s+/g, '_');
}

/** Prefer the declared emotion attribute’s ID value, then accept retained emotion_word/emotion/emotion_label aliases. Missing evidence becomes an empty draft. */
function getEmotionValue(
  customAttributes: Record<string, unknown>,
  emotionAttribute?: AttributeDef
): unknown {
  if (emotionAttribute) {
    const direct = customAttributes[emotionAttribute.attr_id];
    if (direct !== null && direct !== undefined && direct !== '') return direct;
  }
  return customAttributes.emotion_word || customAttributes.emotion || customAttributes.emotion_label || '';
}
